import React, { useState } from "react"
import { Popover } from "radix-ui"
import { HexColorPicker } from "react-colorful"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"

// Preset swatches, arranged to echo the reference palette grid.
const PRESETS = [
  "#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e", "#06b6d4", "#3b82f6", "#a855f7",
  "#f87171", "#fb923c", "#facc15", "#a3e635", "#4ade80", "#38bdf8", "#818cf8", "#c084fc",
  "#b91c1c", "#c2410c", "#a16207", "#4d7c0f", "#15803d", "#0e7490", "#1d4ed8", "#7e22ce",
]

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

// A swatch button that opens a color palette popover.
//   value    = current hex string
//   onChange = next hex string
export function ColorPalettePopover({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [hexDraft, setHexDraft] = useState(value)

  // Keep the hex input in sync when the swatch/picker changes the value.
  React.useEffect(() => { setHexDraft(value) }, [value])

  function commitHex(next) {
    setHexDraft(next)
    if (HEX_RE.test(next)) onChange(next)
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex h-auto items-center gap-2 rounded-md border-slate-200 bg-white px-2.5 py-1.5 text-[13px] font-normal text-slate-700 hover:border-slate-300 hover:bg-white"
        >
          <span
            className="h-5 w-5 shrink-0 rounded border border-black/10"
            style={{ backgroundColor: value }}
          />
          <span className="font-mono uppercase">{value}</span>
        </Button>
      </Popover.Trigger>

      {/* Not portalled: the swatch lives inside the Sheet (a modal Radix
          Dialog), which blocks pointer/focus events to anything portalled to
          <body>. Rendering inline keeps the popover within the dialog's scope so
          the picker is interactive. Widths are inline so they can't be purged. */}
      <Popover.Content
        align="start"
        sideOffset={6}
        style={{ width: 232 }}
        className="z-[60] rounded-xl border border-slate-200 bg-white p-3 shadow-xl"
      >
        {/* react-colorful is fixed 200x200 by its own CSS; override to fill. */}
        <HexColorPicker
          color={value}
          onChange={onChange}
          style={{ width: "100%", height: 160 }}
        />

        {/* Preset swatches */}
        <div
          style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6, marginTop: 12 }}
        >
          {PRESETS.map((c) => (
            <Button
              key={c}
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onChange(c)}
              className="h-auto w-auto p-0 transition-transform hover:scale-110 hover:bg-transparent"
              style={{
                height: 22,
                width: 22,
                borderRadius: 6,
                backgroundColor: c,
                border: `2px solid ${value.toLowerCase() === c ? "#0f172a" : "transparent"}`,
              }}
              aria-label={c}
            />
          ))}
        </div>

        {/* Hex input + done */}
        <div className="mt-3 flex items-center gap-2">
          <Input
            value={hexDraft}
            onChange={(e) => commitHex(e.target.value)}
            placeholder="#64748B"
            className="h-8 font-mono text-[13px] uppercase"
            maxLength={7}
          />
          <Popover.Close asChild>
            <Button
              type="button"
              className="h-8 shrink-0 rounded-md px-3 text-[13px]"
            >
              Save
            </Button>
          </Popover.Close>
        </div>
      </Popover.Content>
    </Popover.Root>
  )
}
